/**
 * [Session C-B CP-4] New Dashboard - news-first home screen.
 *
 * Layout:
 *   1. Top nav with persistent player search
 *   2. LEAGUES + PLAYERS nav tiles (landscape rectangles, side by side)
 *   3. "This Week" hero card (active season only)
 *   4. 3 news feed tiers (My Players / My Leagues / NFL News)
 *
 * Existing league-grid view relocates to /leagues (behind the LEAGUES
 * tile). This dashboard replaces the previous league-grid-centric one.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LogOut, Trophy, Users, ChevronRight, Sun, Moon } from "lucide-react";
import axios from "axios";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import ProfileButton from "../components/ProfileButton";
import AdminButton from "../components/AdminButton";
import NewsFeedTier from "../components/dashboard/NewsFeedTier";
import PlayerSearchOverlay from "../components/dashboard/PlayerSearchOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  // [Session C-B CP-5 Addendum] Full theme compliance. Every panel now
  // has a day/night pair - was hardcoded dark in CP-4.
  const { isDark, toggleTheme } = useTheme();
  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#606060]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";
  const searchBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const searchPh = isDark ? "text-[#606060]" : "text-[#808080]";
  const headerBg = isDark ? "bg-[#141414]" : "bg-white";
  const heroBg = isDark
    ? "bg-gradient-to-br from-[#1C1C1C] to-[#141414]"
    : "bg-gradient-to-br from-white to-[#F5F5F5]";

  const [leagues, setLeagues] = useState([]);
  const [myPlayersFeed, setMyPlayersFeed] = useState({ items: [], last_updated: null });
  const [myLeaguesFeed, setMyLeaguesFeed] = useState({ items: [], last_updated: null });
  const [nflFeed, setNflFeed] = useState({ items: [], last_updated: null });
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/leagues`, { headers: authHeaders() });
        setLeagues(Array.isArray(data) ? data : []);
      } catch { setLeagues([]); }
    })();
    refreshFeed("my-players", setMyPlayersFeed);
    refreshFeed("my-leagues", setMyLeaguesFeed);
    refreshFeed("nfl", setNflFeed);
  }, []);

  const refreshFeed = async (slug, setter) => {
    try {
      const { data } = await axios.get(`${API}/news/${slug}?limit=20`, { headers: authHeaders() });
      setter(data);
    } catch {
      setter({ items: [], last_updated: null });
    }
  };

  const activeCount = leagues.filter((l) => l.season_phase === "active_season" || l.season_phase === "playoffs").length;
  const attentionCount = leagues.filter((l) => l.status === "LOBBY" || l.status === "PAUSED").length;

  const diamondName = null; // populated in a future pass - hero surface for PLAYERS tile
  const activeSeasonLeagues = leagues.filter((l) => l.season_phase === "active_season");
  const currentWeek = 1; // wired from SeasonState in a future pass

  return (
    <div className={`min-h-screen ${bg} ${fg}`}>
      <PlayerSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Top nav */}
      <header className={`border-b ${cardBorder} ${headerBg} sticky top-0 z-30`}>
        <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => nav("/dashboard")}
            className="flex items-center gap-2"
            data-testid="top-nav-home"
          >
            <div className="w-7 h-7 rounded-md bg-[#FF6B00] flex items-center justify-center">
              <span className="font-display font-black text-xs text-black">PGF</span>
            </div>
            <span className={`font-display font-black uppercase text-sm tracking-wider hidden sm:inline ${fg}`}>
              Pregame Fantasy
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg ${searchBg} border ${cardBorder} hover:border-[#FF6B00]/60 transition-colors text-left`}
            data-testid="top-nav-search-trigger"
          >
            <Search size={14} className={muted} />
            <span className={`${searchPh} text-sm`}>Search any NFL player…</span>
          </button>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg border ${cardBorder} hover:bg-[#FF6B00]/10`}
            title="Toggle theme"
            data-testid="top-nav-theme-toggle"
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <AdminButton variant={isDark ? "dark" : "light"} />
          <ProfileButton variant={isDark ? "dark" : "light"} />
          <button
            onClick={logout}
            className={`p-2 ${muted} hover:text-[#FF6B00]`}
            title="Sign out"
            data-testid="top-nav-logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Nav tiles - landscape rectangles, side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="dashboard-nav-tiles">
          <NavTile
            title="Leagues"
            testId="nav-tile-leagues"
            ghostKind="stadium"
            statLabel={
              attentionCount > 0
                ? `${activeCount} active · ${attentionCount} need attention`
                : `${activeCount} active`
            }
            statColor={attentionCount > 0 ? "#FF6B00" : "#00C853"}
            isDark={isDark}
            cardBg={cardBg}
            cardBorder={cardBorder}
            fg={fg}
            onClick={() => nav("/leagues")}
          />
          <NavTile
            title="Players"
            testId="nav-tile-players"
            ghostKind="silhouette"
            statLabel={diamondName || "Explore rankings, edges, and the Diamond"}
            statColor="#00C853"
            isDark={isDark}
            cardBg={cardBg}
            cardBorder={cardBorder}
            fg={fg}
            onClick={() => nav("/players")}
          />
        </div>

        {/* This Week hero - active-season leagues only */}
        {activeSeasonLeagues.length > 0 && (
          <section
            className={`mb-6 rounded-2xl ${heroBg} border ${cardBorder} p-5`}
            data-testid="this-week-hero"
          >
            <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold mb-1">
              This Week
            </div>
            <div className="flex items-baseline gap-3">
              <h2 className={`font-display font-black uppercase text-2xl ${fg}`}>Week {currentWeek}</h2>
              <span className={`${muted} text-sm`}>
                across {activeSeasonLeagues.length} active {activeSeasonLeagues.length === 1 ? "league" : "leagues"}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {activeSeasonLeagues.slice(0, 4).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => nav(`/league/${l.id}/home`)}
                  className={`px-3 py-1.5 rounded-md ${isDark ? "bg-[#2A2A2A] hover:bg-[#FF6B00]/20" : "bg-[#E5E5E5] hover:bg-[#FF6B00]/20"} text-xs font-display font-bold uppercase tracking-wide flex items-center gap-1 ${fg}`}
                  data-testid={`this-week-league-${l.id}`}
                >
                  {l.name}
                  <ChevronRight size={12} />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Three news tiers */}
        <NewsFeedTier
          title="My Players"
          items={myPlayersFeed.items || []}
          lastUpdated={myPlayersFeed.last_updated}
          emptyCTA="Join a league to see personalized player news."
          onRefresh={() => refreshFeed("my-players", setMyPlayersFeed)}
          onSeeAll={() => nav("/news/my-players")}
        />
        <NewsFeedTier
          title="My Leagues"
          items={myLeaguesFeed.items || []}
          lastUpdated={myLeaguesFeed.last_updated}
          emptyCTA="Create or join a league to see activity."
          onRefresh={() => refreshFeed("my-leagues", setMyLeaguesFeed)}
          onSeeAll={() => nav("/news/my-leagues")}
        />
        <NewsFeedTier
          title="NFL News"
          items={nflFeed.items || []}
          lastUpdated={nflFeed.last_updated}
          emptyCTA="News updates during the NFL season."
          onRefresh={() => refreshFeed("nfl", setNflFeed)}
          onSeeAll={() => nav("/news/nfl")}
        />
      </main>
    </div>
  );
}

function NavTile({ title, ghostKind, statLabel, statColor, testId, isDark, cardBg, cardBorder, fg, onClick }) {
  // [Session C-B CP-5 Addendum] Ghost background - inline SVG at ~18%
  // opacity. `stadium` = concentric stadium tier arcs + field. `silhouette`
  // = a helmeted-shoulder player figure. Inline SVG keeps this
  // asset-dependency-free and instantly responsive to theme.
  const ghostColor = isDark ? "rgba(245,245,245,0.18)" : "rgba(17,17,17,0.18)";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl ${cardBg} border ${cardBorder} hover:border-[#FF6B00]/60 transition-colors text-left aspect-[16/7] w-full flex items-center justify-between p-6`}
      data-testid={testId}
    >
      {/* Ghost image - absolute, behind text */}
      <div className="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none">
        {ghostKind === "stadium" ? (
          <svg viewBox="0 0 200 100" className="h-full w-auto" fill="none" stroke={ghostColor} strokeWidth="1.5">
            {/* Concentric stadium bowls */}
            <ellipse cx="100" cy="70" rx="90" ry="24" />
            <ellipse cx="100" cy="70" rx="72" ry="19" />
            <ellipse cx="100" cy="70" rx="55" ry="14" />
            {/* Field */}
            <rect x="60" y="63" width="80" height="14" rx="1" />
            <line x1="80"  y1="63" x2="80"  y2="77" />
            <line x1="100" y1="63" x2="100" y2="77" />
            <line x1="120" y1="63" x2="120" y2="77" />
            {/* Lights */}
            <line x1="20" y1="20" x2="20" y2="46" />
            <line x1="180" y1="20" x2="180" y2="46" />
            <circle cx="20" cy="16" r="4" fill={ghostColor} />
            <circle cx="180" cy="16" r="4" fill={ghostColor} />
          </svg>
        ) : (
          <svg viewBox="0 0 100 120" className="h-full w-auto" fill={ghostColor}>
            {/* Helmeted player silhouette - helmet + shoulder pads */}
            <path d="M50 12 C 32 12, 22 26, 22 42 C 22 50, 24 56, 28 60 L 28 66 C 20 68, 14 72, 14 78 L 14 120 L 86 120 L 86 78 C 86 72, 80 68, 72 66 L 72 60 C 76 56, 78 50, 78 42 C 78 26, 68 12, 50 12 Z" />
            {/* Facemask negative-space bar */}
            <rect x="24" y="46" width="52" height="4" fill={isDark ? "#1C1C1C" : "#FFFFFF"} opacity="0.9" />
          </svg>
        )}
      </div>
      <div className="relative z-10">
        <h2 className={`font-display font-black uppercase text-3xl sm:text-4xl tracking-tight ${fg}`}>
          {title}
        </h2>
        <div
          className="mt-2 text-xs uppercase tracking-widest font-display font-bold"
          style={{ color: statColor }}
        >
          {statLabel}
        </div>
      </div>
      <ChevronRight size={20} className={`absolute right-4 top-4 ${isDark ? "text-[#404040]" : "text-[#B0B0B0]"} group-hover:text-[#FF6B00]`} />
    </button>
  );
}
